import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { ProductsSection } from 'src/components/products-section';

import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import { productNavigation } from '../../actions/product';
import { trackProductRecommendationClicked } from 'src/components/recommendations/tracking';

import {
  OTHERS_ALSO_VISITED_PRODUCT_CLICK,
  OTHER_POPULAR_PRODUCT_CLICK,
  TRENDING_PRODUCT_CLICK,
} from 'src/globals/helpers/navigation-action';
import { PRODUCT_CONTEXT_ID } from '../../constants';
import { CarouselTypeEnum } from './types';
import type {
  Product,
  CarouselProduct as CarouselProductType,
} from 'src/types';

type Props = {
  carouselType: CarouselTypeEnum;
  contextId?: string;
  products: CarouselProductType[];
  title?: string;
};

function getInternalNavigationScope(carouselType: CarouselTypeEnum) {
  switch (carouselType) {
    case CarouselTypeEnum.OTHER_POPULAR_PRODUCTS:
      return InternalNavigationScope.OTHER_PRODUCTS_PRODUCT_CARD;
    case CarouselTypeEnum.OTHERS_VISITED:
      return InternalNavigationScope.OTHER_VISITED_PRODUCTS_PRODUCT_CARD;
    case CarouselTypeEnum.TRENDING_PRODUCTS:
      return InternalNavigationScope.TRENDING_PRODUCTS_PRODUCT_CARD;
    default:
      break;
  }

  return InternalNavigationScope.TRENDING_PRODUCTS_PRODUCT_CARD;
}

function getNavigationAction(carouselType: CarouselTypeEnum) {
  switch (carouselType) {
    case CarouselTypeEnum.OTHERS_VISITED:
      return OTHERS_ALSO_VISITED_PRODUCT_CLICK;
    case CarouselTypeEnum.OTHER_POPULAR_PRODUCTS:
      return OTHER_POPULAR_PRODUCT_CLICK;
    case CarouselTypeEnum.TRENDING_PRODUCTS:
      return TRENDING_PRODUCT_CLICK;
    default:
      break;
  }

  return TRENDING_PRODUCT_CLICK;
}

export const CarouselProducts: React.FC<Props> = ({
  carouselType,
  contextId = PRODUCT_CONTEXT_ID,
  products,
  title = '',
}) => {
  const dispatch = useDispatch();

  const mappedProducts: Product[] | null = useMemo(() => {
    if (!products) return null;

    return products
      .filter((tp): tp is CarouselProductType => Boolean(tp))
      .filter((tp: CarouselProductType) => Boolean(tp.pathName))
      .map(product => ({
        id: Number(product.id),
        name: String(product.name),
        pathName: product.pathName ?? '',
        media: { first: product.imageUrl, count: 1 },
        priceSummary: { regular: product.price },
        classicFullPath: String(product.url),
        userReviewSummary: product.userReviewSummary,
        aggregatedRating: product.aggregatedRating,
      }));
  }, [products]);

  if (!mappedProducts?.length) return null;

  const handleProductClick = (
    index: number,
    link: string,
    productId: number
  ) => {
    dispatch(productNavigation(getNavigationAction(carouselType), index + 1));

    trackProductRecommendationClicked(contextId, productId, carouselType);
    trackInternalNavigationClick({
      scope: getInternalNavigationScope(carouselType),
      nextPage: link,
      index,
    });
  };

  return (
    <ProductsSection
      negativeMargin
      products={mappedProducts}
      title={title}
      onProductCardClick={({ index, link, productId }) =>
        handleProductClick(index, link, productId)
      }
    />
  );
};
